import React, {useEffect, useCallback} from 'react';
import ReactGA from 'react-ga4';
import PageTitle from './PageTitle';
import Alerts from './Alerts';
import Products from './Products';
import Aside from './Aside';
import { loadOrder, leaveOrder, changeQueryTab, chaseOrder, chaseProduct, closeReportDialog, raiseQuery, cancelOrder } from './actions';
import { uploadRequest, uploadDeleteRequest } from '../../components/UploadFile/actions';
import { useSelector, useDispatch } from 'react-redux';
import {
    selectOrder, selectAttachments,
    selectActiveQueryTab, selectPackagesCount,
    selectOpenQueryCount, selectClosedQueryCount, selectReports,
    selectOrderChaseButtonState, selectProductChaseButtonState, selectIsReportDialogOpen, selectRaiseButtonVisibility, selectOrderCancelButtonState,
    selectOrderLoadFailed, selectRequestedOrderId
} from './selectors';
import { selectIsAccountCustomer } from '../App/selectors';
import GridQueriesInOrderPage from './grids/GridQueriesInOrderPage';
import GridQueriesInOrderPageClosed from './grids/GridQueriesInOrderPageClosed';
import QueriesTitle from './QueriesTitle';
import { Link, useParams } from 'react-router-dom';
import ReportDialog from './ReportDialog';
import { OpenQueryGridName } from './constants';
import ExpandableSection from '../../components/ExpandableSection';
import Loading from '../../components/Loading';
import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import rootSaga from './sagas';
import detailsReducer, { isSameOrderId } from './reducer';
//import { RESTART_ON_REMOUNT } from '../../utils/constants';

const orderSelector= selectOrder();
const attachmentsSelector= selectAttachments();
const activeQueryTabSelector= selectActiveQueryTab();
const productCountSelector= selectPackagesCount();
const openQueryCountSelector= selectOpenQueryCount();
const closedQueryCountSelector= selectClosedQueryCount();
const orderChaseButtonStateSelector= selectOrderChaseButtonState();
const orderCancelButtonStateSelector= selectOrderCancelButtonState();
const productChaseButtonStateSelector= selectProductChaseButtonState();
const isReportDialogOpenSelector= selectIsReportDialogOpen();
const reportsSelector= selectReports();
const isRaiseButtonVisibileSelector= selectRaiseButtonVisibility();
const isAccountCustomerSelector= selectIsAccountCustomer();
const orderLoadFailedSelector= selectOrderLoadFailed();
const requestedOrderIdSelector= selectRequestedOrderId();

export default function OrderDetails(props)/* extends React.PureComponent */ {
    useInjectReducer({ key: 'order-details', reducer: detailsReducer });
    useInjectSaga({ key: 'order-details', saga: rootSaga/*, mode: RESTART_ON_REMOUNT*/ });
    const order= useSelector(orderSelector);
    const attachments= useSelector(attachmentsSelector);
    const activeQueryTab= useSelector(activeQueryTabSelector);
    const productCount= useSelector(productCountSelector);
    const openQueryCount= useSelector(openQueryCountSelector);
    const closedQueryCount= useSelector(closedQueryCountSelector);
    const orderChaseButtonState= useSelector(orderChaseButtonStateSelector);
    const orderCancelButtonState= useSelector(orderCancelButtonStateSelector);
    const productChaseButtonState= useSelector(productChaseButtonStateSelector);
    const isReportDialogOpen= useSelector(isReportDialogOpenSelector);
    const reports= useSelector(reportsSelector);
    const isRaiseButtonVisibile= useSelector(isRaiseButtonVisibileSelector);
    const isAccountCustomer= useSelector(isAccountCustomerSelector);
    const orderLoadFailed= useSelector(orderLoadFailedSelector);
    const requestedOrderId= useSelector(requestedOrderIdSelector);
   
    const dispatch = useDispatch();
    const loadOrderAction = useCallback((orderId) => dispatch(loadOrder(orderId)), [dispatch]);
    const chaseOrderAction = useCallback((info) => dispatch(chaseOrder(info)), [dispatch]);
    const chaseProductAction = useCallback((item) => dispatch(chaseProduct(item)), [dispatch]);
    const uploadRequestAction = useCallback((file, objectId, attachmentType, uploadChannel, options) => dispatch(uploadRequest(file, objectId, attachmentType, uploadChannel, options)), [dispatch]);
    const deleteFileAction = useCallback((fileName, objectId, uploadChannel) => dispatch(uploadDeleteRequest(fileName, objectId, uploadChannel)), [dispatch]);
    const changeQueryTabAction = useCallback((tabName) => dispatch(changeQueryTab(tabName)), [dispatch]);
    const closeReportDialogAction = useCallback(() => dispatch(closeReportDialog()), [dispatch]);
    const raiseQueryAction = useCallback((orderId, gridName, rowTemplateKey) => dispatch(raiseQuery(orderId, gridName, rowTemplateKey)), [dispatch]);
    const cancelOrderAction = useCallback((item) => dispatch(cancelOrder(item)), [dispatch]);

    let { id } = useParams();
    // Reload when the route id changes: navigating /order/A -> /order/B (e.g. from a notification)
    // reuses this component, so a mount-only effect would keep showing order A.
    useEffect(() => {
        loadOrderAction(id);
        ReactGA.send({ hitType: "pageview", page: "/order" });
    }, [id, loadOrderAction]);
    // Leaving the page cancels the order's in-flight work, and reopening it later starts clean.
    useEffect(() => () => dispatch(leaveOrder()), [dispatch]);

    function handleProductChase(product) {

        const item = {
            orderId: order.orderId,
            packageId: product.id,
            dueDate: product.dueDate
        };

        chaseProductAction(item);
    }

    function handleChase() {
        const info = {
            orderId: order.orderId,
            dueDate: order.dueDate
        };

        chaseOrderAction(info);
    }

    function handleRaiseQuery() {
        raiseQueryAction(order.orderId, OpenQueryGridName, 'queryDetails');
    }

    function handleCancelQuery() {
        cancelQueryAction(order.orderId, OpenQueryGridName, 'queryDetails');
    }

    function _renderAddMorePackages(status) {
        if (isAccountCustomer != true) {
            return null;
        }

        if (status == 'Completed') {
            return null;
        }

        return <Link key={2} to={`/add-products/${order.orderId}`} className="button button-small button-green hide-mobile">Add More Products</Link>;
    }

    function _renderTitle() {
        const status = order.status;
        var arr = [];
        arr.push(<h2 key={1} id="products-count">Products ({productCount})</h2>);
        arr.push(_renderAddMorePackages(status));
        return arr;
    }

    function hideShowMoreProducts() {
        const status = order.status;
        return (status === 'Completed') || (status === 'NotStarted') || (status === 'InProgress');
    }

    // Don't show (or act on) the previous order while the one in the URL is loading or failed to load.
    // The failure itself is reported by the app's error toast; offer a retry here so the page isn't left empty.
    // Data kept from an earlier visit (see the reducer) isn't shown either: this page's own load replaces it.
    if(order==undefined || !isSameOrderId(order.orderId, id) || !isSameOrderId(requestedOrderId, id)) {
        return (
            <div className="content-wrapper details-wrapper">
                <section className="details-main">
                    <div className="content-block">
                        {orderLoadFailed && isSameOrderId(requestedOrderId, id) ?
                            <div id="order-load-failed" style={{ padding: 20 }}>
                                <p>Order {id} could not be loaded.</p>
                                <button className="button button-small button-green" onClick={() => loadOrderAction(id)}>Retry</button>
                                {' '}<Link to="/orders" className="text-link">Back to orders</Link>
                            </div>
                            :
                            <Loading />
                        }
                    </div>
                </section>
            </div>
        );
    }

    return (
        <div>
            <PageTitle order={order}
                handleChase={handleChase}
                orderChaseButtonState={orderChaseButtonState}
                orderCancelButtonState={orderCancelButtonState}
                raiseQueryAction={handleRaiseQuery}
                cancelOrderAction={cancelOrderAction}
                isAccountCustomer={isAccountCustomer} />
            <div className="content-wrapper details-wrapper">

                <section className="details-main">
                    {/* Products */}
                    <div className="content-block">
                        {hideShowMoreProducts() ?
                            <ExpandableSection className='' titleContent={_renderTitle()} isExpandable={true}>
                                <Alerts orderId={order.orderId}
                                    status={order.status}
                                />
                            </ExpandableSection> : null}
                        <Products 
                            order={order}
                            
                            //loadPackageReports = {loadPackageReports}
                            handleProductChase={handleProductChase}
                            productChaseButtonState={productChaseButtonState} />
                    </div>
                    {/* Queries */}
                    <div className="content-block">
                        <QueriesTitle activeTab={activeQueryTab}
                            tabChanged={changeQueryTabAction}
                            openQueryCount={openQueryCount}
                            closedQueryCount={closedQueryCount}
                            raiseQueryAction={handleRaiseQuery}
                            isRaiseButtonVisibile={isRaiseButtonVisibile} />
                        <div className="content-block-grid">
                            {activeQueryTab == 'open' ?
                                <GridQueriesInOrderPage />
                                :
                                <GridQueriesInOrderPageClosed />
                            }
                        </div>
                    </div>
                </section>
                <Aside order={order} addAttachmentToOrder={uploadRequestAction}
                    deleteAttachmentFromOrder={deleteFileAction} attachments={attachments} />
            </div>
            <ReportDialog open={isReportDialogOpen} reports={reports} closeDialog={closeReportDialogAction} />
        </div>
    )
}